import { useState, type ReactNode } from 'react';
import { Modal } from '../ui/Modal';
import { useStore } from '@/store/useStore';
import {
  ChevronLeft, ChevronRight, Check, Sparkles, Briefcase, Calendar, BarChart3, BookOpen, Plug,
  Inbox, MousePointer2, Copy, Trash2, Keyboard, Plus, Search, Filter,
} from 'lucide-react';
import { cn } from '@/utils/cn';

interface Step {
  icon: typeof Sparkles;
  title: string;
  /** на какой странице показывать (тур переключит) */
  page?: 'crm' | 'calendar' | 'dashboard' | 'directory';
  body: ReactNode;
}

function Kbd({ children }: { children: ReactNode }) {
  return <kbd className="px-1.5 py-0.5 rounded bg-surface-2 text-xs font-mono border border-line">{children}</kbd>;
}

const STEPS: Step[] = [
  {
    icon: Sparkles,
    title: '👋 Добро пожаловать в TeamScheduler CRM',
    body: (
      <>
        <p className="mb-2">Этот тур из <b>{0 || 18} шагов</b> покажет каждый раздел и каждую кнопку приложения.</p>
        <p className="text-ink-2">
          Управление: <Kbd>→</Kbd> / <Kbd>←</Kbd> между шагами, <Kbd>Esc</Kbd> чтобы закрыть.
          Можно открыть тур в любой момент кнопкой <b>?</b> в боковом меню или клавишей <Kbd>F1</Kbd>.
        </p>
      </>
    ),
  },
  {
    icon: Briefcase,
    title: 'CRM — список сделок',
    page: 'crm',
    body: (
      <>
        <p className="mb-2">Главный экран — все сделки в виде карточек.</p>
        <ul className="list-disc list-inside text-ink-2 space-y-1">
          <li>Фильтры в верхней мини-панели: <b>Отдел</b> (B2C / Ивент), <b>Статус</b> (новая, в работе, оплачена, …)</li>
          <li>Цвет полосы слева — цвет филиала</li>
          <li>Клик по карточке → открывает редактор сделки</li>
          <li>Бейдж справа — текущий статус сделки</li>
        </ul>
      </>
    ),
  },
  {
    icon: Plus,
    title: 'Создать сделку',
    body: (
      <>
        <p>Кнопка <b>«Новая сделка»</b> в правом верхнем углу или <Kbd>Ctrl + N</Kbd>.</p>
        <p className="text-ink-2 mt-2">Карточка открывается заполненной: автоматически выдаётся <b>номер</b> (B2C-2026-0001 или EVT-2026-0001), подставляется текущий филиал и валюта.</p>
      </>
    ),
  },
  {
    icon: Briefcase,
    title: 'Карточка сделки — блок «Сделка»',
    body: (
      <>
        <p>Все базовые поля для регистрации заявки:</p>
        <ul className="list-disc list-inside text-ink-2 space-y-1 mt-2">
          <li>Номер (auto), тип (B2C / Ивент), статус</li>
          <li>Сумма + валюта (тг / сум / манат)</li>
          <li>Тип сделки (тег)</li>
          <li>ФИО и телефон клиента</li>
          <li>Источник, филиал, ответственный</li>
          <li>Команда (для отображения в календаре)</li>
        </ul>
      </>
    ),
  },
  {
    icon: Briefcase,
    title: 'Карточка — «Оплата»',
    body: (
      <>
        <p>Два блока: <b>Предоплата</b> и <b>Остаток</b>.</p>
        <p className="text-ink-2 mt-2">При указанной сумме нужно <b>обязательно</b> прикрепить чек (jpg/png/pdf).
          Если суммы нет — чек не нужен. Прикрепить можно несколько файлов.</p>
        <p className="text-ink-3 mt-2 text-xs">⚠ Если есть сумма, но нет чека — кнопка «Сохранить» заблокируется.</p>
      </>
    ),
  },
  {
    icon: Calendar,
    title: 'Карточка — «О заказе»',
    body: (
      <>
        <p>Все детали мероприятия:</p>
        <ul className="list-disc list-inside text-ink-2 space-y-1 mt-2">
          <li>Повод (выборка) + признак <b>ежегодного напоминания</b> (для ДР)</li>
          <li>Дата мероприятия — её используют календарь и дашборд</li>
          <li>Место (выезд / студия) + адрес с ссылкой на 2GIS</li>
          <li>Кол-во и возраст участников</li>
          <li>Услуги, формат игры, играли ли ранее</li>
          <li>Длительность, время начала/завершения, язык</li>
          <li>Команда (текстом) — записывается вручную, может отличаться от привязки к команде</li>
        </ul>
      </>
    ),
  },
  {
    icon: Briefcase,
    title: 'Отказ',
    body: (
      <>
        <p>Если статус сделки = <b>«Отказ»</b>, появляется блок:</p>
        <ul className="list-disc list-inside text-ink-2 space-y-1 mt-2">
          <li>Причина (выборка из справочника)</li>
          <li>Дата отказа</li>
          <li>Комментарий</li>
        </ul>
        <p className="text-ink-3 mt-2 text-xs">Отказы не показываются в календаре, но видны в дашборде для анализа.</p>
      </>
    ),
  },
  {
    icon: Calendar,
    title: 'Календарь — общая идея',
    page: 'calendar',
    body: (
      <>
        <p className="mb-2">Календарь — это <b>главная рабочая зона</b>:</p>
        <ul className="list-disc list-inside text-ink-2 space-y-1">
          <li>Слева — <b>очередь</b> входящих заявок без даты</li>
          <li>Справа — сетка <b>филиалы × дни</b></li>
          <li>Тащишь карточку из очереди или существующий слот → ставится на нужный день и филиал</li>
        </ul>
      </>
    ),
  },
  {
    icon: Inbox,
    title: 'Очередь входящих',
    body: (
      <>
        <p>Здесь автоматически появляются все сделки <b>без даты мероприятия</b> (статус ≠ Отказ / Завершена).</p>
        <p className="text-ink-2 mt-2">Бейдж <b>«N нов.»</b> сверху показывает количество новых заявок, которые ждут распределения.</p>
        <p className="text-ink-2 mt-2">Перетаскиваете карточку курсором → она ложится на выбранный день. Время по умолчанию — 18:00–20:00 (можно поменять в карточке).</p>
      </>
    ),
  },
  {
    icon: MousePointer2,
    title: 'Drag-and-drop в календаре',
    body: (
      <>
        <p className="mb-2">DnD во все стороны:</p>
        <ul className="list-disc list-inside text-ink-2 space-y-1">
          <li>Слот → <b>другой день или филиал</b> — все поля времени сдвинутся автоматически</li>
          <li>Карточка из очереди → <b>день × филиал</b> в сетке (ставит время по умолчанию 18:00–20:00)</li>
          <li>Слот → <b>обратно в очередь слева</b> — если передумал, дата и время сбрасываются, сделка снова в «новых»</li>
          <li>Двойной клик по пустой ячейке → быстро создать сделку с предзаполненной датой и филиалом</li>
        </ul>
      </>
    ),
  },
  {
    icon: Sparkles,
    title: 'Цвета слотов = статус сделки',
    body: (
      <>
        <p>Цвет слота автоматически меняется при смене статуса:</p>
        <ul className="list-none text-ink-2 space-y-1 mt-2 text-xs">
          <li><span className="inline-block w-3 h-3 rounded-sm align-middle mr-2" style={{ backgroundColor: '#378ADD' }} />Новая</li>
          <li><span className="inline-block w-3 h-3 rounded-sm align-middle mr-2" style={{ backgroundColor: '#639922' }} />В работе</li>
          <li><span className="inline-block w-3 h-3 rounded-sm align-middle mr-2" style={{ backgroundColor: '#F59E0B' }} />Частично оплачена</li>
          <li><span className="inline-block w-3 h-3 rounded-sm align-middle mr-2" style={{ backgroundColor: '#16A34A' }} />Оплачена</li>
          <li><span className="inline-block w-3 h-3 rounded-sm align-middle mr-2" style={{ backgroundColor: '#888780' }} />Завершена</li>
          <li><span className="inline-block w-3 h-3 rounded-sm align-middle mr-2" style={{ backgroundColor: '#E24B4A' }} />Отказ</li>
        </ul>
        <p className="text-ink-3 mt-2 text-xs">Цвет команды (если назначена) — узкая полоса сверху слота. Прогресс оплаты — белая полоса снизу.</p>
      </>
    ),
  },
  {
    icon: MousePointer2,
    title: 'Контекстное меню (ПКМ)',
    body: (
      <>
        <p>Правый клик по любой сделке (в очереди или в календаре) → меню действий:</p>
        <ul className="list-disc list-inside text-ink-2 space-y-1 mt-2">
          <li><b>Открыть</b> — карточка сделки</li>
          <li><b>Дублировать</b> — создаст копию с новым номером</li>
          <li><b>Сменить статус</b> → любой из 6 статусов одним кликом</li>
          <li><b>Удалить</b></li>
        </ul>
      </>
    ),
  },
  {
    icon: BarChart3,
    title: 'Дашборд',
    page: 'dashboard',
    body: (
      <>
        <p>4 KPI-карточки + 2 разбивки (по статусам и по филиалам).</p>
        <p className="text-ink-2 mt-2">Учитывается <b>часовой пояс филиала</b> при подсчёте «сегодня» — сделка по Баку считается за бакинский день, по Алматы — за алматинский.</p>
        <p className="text-ink-3 mt-2 text-xs">Глобальный фильтр филиала наверху работает на всех страницах сразу.</p>
      </>
    ),
  },
  {
    icon: BookOpen,
    title: 'Справочники',
    page: 'directory',
    body: (
      <>
        <p>Слева — 5 вкладок:</p>
        <ul className="list-disc list-inside text-ink-2 space-y-1 mt-2">
          <li><b>Филиалы</b> — название, город, цвет, часовой пояс</li>
          <li><b>Сотрудники</b> — ФИО, филиал, роль, контакты</li>
          <li><b>Команды</b> — состав из сотрудников выбранного филиала</li>
          <li><b>Списки</b> — источники, поводы, форматы игры, причины отказа</li>
          <li><b>Bitrix24</b> — настройки интеграции</li>
        </ul>
      </>
    ),
  },
  {
    icon: Plug,
    title: 'Интеграция с Bitrix24',
    body: (
      <>
        <p>На 1-м этапе всё работает <b>локально</b> (IndexedDB на вашем компьютере). На 2-м этапе можно подключить ваш портал Bitrix24:</p>
        <ul className="list-disc list-inside text-ink-2 space-y-1 mt-2">
          <li>Вводите URL входящего вебхука</li>
          <li>«Проверить связь» → видите имя пользователя</li>
          <li>Сохраняете → начинается двусторонняя синхронизация сделок, контактов, календаря, чеков и справочников</li>
        </ul>
        <p className="text-ink-3 mt-2 text-xs">Сейчас доступна только проверка соединения. Полная синхронизация будет добавлена на 2-м спринте — каркас и маппинг полей уже спроектированы.</p>
      </>
    ),
  },
  {
    icon: Search,
    title: 'Поиск и фильтры',
    body: (
      <>
        <p>В верхней панели:</p>
        <ul className="list-disc list-inside text-ink-2 space-y-1 mt-2">
          <li><b>Поиск</b> (<Kbd>Ctrl + F</Kbd>) — по номерам, ФИО, телефонам, адресам</li>
          <li><b>Филиал</b> — глобальный фильтр для всех страниц</li>
        </ul>
        <p className="text-ink-2 mt-2">На странице CRM есть дополнительные фильтры (отдел, статус), на Календаре — фильтр по команде.</p>
      </>
    ),
  },
  {
    icon: Keyboard,
    title: 'Горячие клавиши',
    body: (
      <div className="grid grid-cols-2 gap-y-1.5 text-xs">
        <Hk k="Ctrl + N" v="новая сделка" />
        <Hk k="Ctrl + F" v="поиск" />
        <Hk k="Ctrl + 1/2/3/4" v="переход CRM/Кален/Дашб/Спр" />
        <Hk k="? или F1" v="открыть тур" />
        <Hk k="Esc" v="закрыть модалку" />
        <Hk k="←/→" v="день / неделя назад/вперёд (в календаре)" />
        <Hk k="Home, T" v="вернуться к сегодня" />
        <Hk k="D / W" v="вид Day / Week (календарь)" />
        <Hk k="Ctrl + D" v="дублировать сделку" />
        <Hk k="ПКМ" v="контекстное меню сделки" />
        <Hk k="Двойной клик по ячейке" v="новая сделка с этой датой/филиалом" />
      </div>
    ),
  },
  {
    icon: Check,
    title: '🎉 Готово!',
    body: (
      <>
        <p className="mb-2">Это всё. Можно начинать работу.</p>
        <p className="text-ink-2">Кнопка <b>?</b> в боковом меню или <Kbd>F1</Kbd> запустят тур заново в любой момент.</p>
      </>
    ),
  },
];

function Hk({ k, v }: { k: string; v: string }) {
  return (
    <>
      <Kbd>{k}</Kbd>
      <span className="text-ink-2 pl-2">{v}</span>
    </>
  );
}

export function HelpModal({ onClose }: { onClose: () => void }) {
  const setPage = useStore((s) => s.setPage);
  const [step, setStep] = useState(0);
  const cur = STEPS[step];
  const Icon = cur.icon;
  const last = step === STEPS.length - 1;

  // Если у шага есть .page — переключаем страницу под подсказкой
  if (cur.page) setTimeout(() => setPage(cur.page!), 0);

  return (
    <Modal
      open onClose={onClose} title="Обучение" size="lg"
      footer={
        <>
          <span className="text-xs text-ink-3 mr-auto tabular-nums">Шаг {step + 1} из {STEPS.length}</span>
          <button className="btn-ghost" disabled={step === 0} onClick={() => setStep((s) => Math.max(0, s - 1))}>
            <ChevronLeft className="w-4 h-4" /> Назад
          </button>
          {last
            ? <button className="btn-primary" onClick={onClose}><Check className="w-4 h-4" /> Готово</button>
            : <button className="btn-primary" onClick={() => setStep((s) => Math.min(STEPS.length - 1, s + 1))}>
              Далее <ChevronRight className="w-4 h-4" />
            </button>}
        </>
      }
    >
      <div className="flex gap-4 items-start">
        <div className="w-12 h-12 rounded-xl bg-accent-soft text-accent grid place-items-center flex-shrink-0">
          <Icon className="w-6 h-6" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-semibold text-ink-1 mb-2">{cur.title}</h3>
          <div className="text-sm text-ink-1 leading-relaxed">{cur.body}</div>
        </div>
      </div>

      <div className="flex items-center justify-center gap-1.5 mt-6">
        {STEPS.map((_, i) => (
          <button key={i} onClick={() => setStep(i)}
            className={cn('h-1.5 rounded-full transition-all',
              i === step ? 'w-6 bg-accent' : 'w-1.5 bg-surface-3 hover:bg-ink-3')} />
        ))}
      </div>
    </Modal>
  );
}
